import { createContext, useContext, useState, useEffect } from 'react';
import { db, collection, onSnapshot } from './firebase/config';

// ── AppContext ────────────────────────────────────────────────────────────────
// Provides user, profile, showToast + globally cached shared data.
// Shared data (users, customers) is fetched ONCE on login via onSnapshot
// and shared across all modules — eliminating redundant per-module fetches.
//
// Usage: const { user, profile, showToast, users, customers } = useAppContext();

export const AppContext = createContext(null);

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used inside AppContext.Provider');
  return ctx;
}

// ── Global data provider — add to App.jsx's AppContext.Provider value ─────────
// Call this hook in App.jsx and spread results into Provider value
export function useSharedData(user) {
  const [users,          setUsers]          = useState([]);
  const [customers,      setCustomers]      = useState([]);
  const [usersLoaded,    setUsersLoaded]    = useState(false);
  const [customersLoaded,setCustomersLoaded]= useState(false);

  useEffect(() => {
    if (!user) {
      // Clear on logout
      setUsers([]); setCustomers([]);
      setUsersLoaded(false); setCustomersLoaded(false);
      return;
    }

    // Live subscription — stays fresh, uses Firestore offline cache for instant first load
    const unsubUsers = onSnapshot(
      collection(db, 'users'),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const ids = new Set(all.map(u => u.id));
        // Hide a superseded (_migratedTo) doc ONLY if its replacement UID doc
        // still exists. If the replacement was deleted, keep the old doc visible
        // so the staff member never disappears entirely.
        setUsers(all.filter(u => !(u._migratedTo && ids.has(u._migratedTo))));
        setUsersLoaded(true);
      },
      () => setUsersLoaded(true) // on error, mark loaded so modules don't hang
    );

    const unsubCustomers = onSnapshot(
      collection(db, 'customers'),
      snap => {
        setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCustomersLoaded(true);
      },
      () => setCustomersLoaded(true)
    );

    return () => { unsubUsers(); unsubCustomers(); };
  }, [user?.uid]);

  return { users, customers, usersLoaded, customersLoaded };
}
